import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Package, FileText, Download, ChevronDown, ChevronRight, Trash2, EyeOff } from 'lucide-react';

export function InvoicesPanel({
  uninvoicedItems = [],
  invoices = [],
  onCreateInvoice,
  onDownloadPdf,
  onMarkPaid,
  onMarkUnpaid,
  onDeleteInvoice,
  onDeletePackage,
  onIgnoreItem,
  canDelete = false,
  canPay = false,
  canIgnore = false,
  t,
}) {
  const [selectedIds, setSelectedIds] = useState({});
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  const unpaidInvoices = invoices.filter(i => !i.paid);
  const paidInvoices = invoices.filter(i => i.paid);
  const selectedCount = Object.keys(selectedIds).filter(k => selectedIds[k]).length;

  const handleCreateInvoice = () => {
    const selected = Object.entries(selectedIds)
      .filter(([, v]) => v)
      .map(([key]) => {
        const item = uninvoicedItems.find(i => i._id === key);
        return item ? { id: item._id, type: item.type } : null;
      })
      .filter(Boolean);
    if (onCreateInvoice) onCreateInvoice(selected);
    setSelectedIds({});
  };

  const toggleInvoice = (id) => setExpandedInvoice(prev => prev === id ? null : id);

  return (
    <div className="space-y-6">
      {/* ===== 1. NON FACTURÉS ===== */}
      <Card className="border-slate-200 shadow-sm" data-testid="uninvoiced-packages-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package size={18} className="text-slate-600" />
              <CardTitle className="text-base font-medium">{t('uninvoiced_packages')}</CardTitle>
              {uninvoicedItems.length > 0 && (
                <Badge className="bg-slate-200 text-slate-700 border-0 text-xs">{uninvoicedItems.length}</Badge>
              )}
            </div>
            {selectedCount > 0 && (
              <Button data-testid="create-invoice-btn" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={handleCreateInvoice}>
                <FileText size={14} /> {t('create_invoice')} ({selectedCount})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {uninvoicedItems.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-3">{t('no_invoices')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-8"></TableHead>
                  <TableHead className="font-medium">{t('orders_label')}</TableHead>
                  <TableHead className="font-medium">{t('weight_kg')}</TableHead>
                  <TableHead className="font-medium">{t('price_vnd')}</TableHead>
                  <TableHead className="font-medium">{t('invoice_date')}</TableHead>
                  <TableHead className="font-medium"></TableHead>
                  {canDelete && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {uninvoicedItems.map(item => (
                  <TableRow key={item._id} className={`hover:bg-slate-50 ${item.type === 'package' ? 'bg-blue-50/30' : ''}`}>
                    <TableCell>
                      <Checkbox
                        data-testid={`select-item-${item._id}`}
                        checked={!!selectedIds[item._id]}
                        onCheckedChange={() => setSelectedIds(prev => ({ ...prev, [item._id]: !prev[item._id] }))}
                      />
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {item.type === 'package' && <Package size={12} className="inline mr-1 text-blue-500" />}
                      {item.order_numbers?.join(', ')}
                    </TableCell>
                    <TableCell className="text-xs">{item.weight_kg} kg</TableCell>
                    <TableCell className="text-xs font-semibold">{item.price_vnd?.toLocaleString()}đ</TableCell>
                    <TableCell className="text-xs">{(item.created_at || '').slice(0, 10)}</TableCell>
                    <TableCell>
                      {canIgnore && (
                        <Button data-testid={`ignore-item-${item._id}`} size="sm" variant="ghost" className="h-6 text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 gap-1"
                          onClick={() => onIgnoreItem?.(item._id, item.type)}>
                          <EyeOff size={12} /> {t('ignore') || 'Ignorer'}
                        </Button>
                      )}
                    </TableCell>
                    {canDelete && (
                      <TableCell>
                        {item.type === 'package' && (
                          <Button data-testid={`delete-package-${item._id}`} size="sm" variant="ghost" className="h-6 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                            onClick={() => onDeletePackage?.(item._id)}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ===== 2. FACTURES EN ATTENTE ===== */}
      {unpaidInvoices.length > 0 && (
        <Card className="border-amber-200 shadow-sm" data-testid="unpaid-invoices-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-amber-600" />
              <CardTitle className="text-base font-medium">{t('invoice_pending')}</CardTitle>
              <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">{unpaidInvoices.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50/50">
                  <TableHead className="font-medium">{t('invoice_number')}</TableHead>
                  <TableHead className="font-medium">{t('invoice_date')}</TableHead>
                  <TableHead className="font-medium">{t('invoice_packages')}</TableHead>
                  <TableHead className="font-medium">{t('invoice_weight')}</TableHead>
                  <TableHead className="font-medium">{t('invoice_total')}</TableHead>
                  <TableHead className="font-medium">PDF</TableHead>
                  {canPay && <TableHead className="font-medium">{t('invoice_paid')}</TableHead>}
                  {canDelete && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {unpaidInvoices.map(inv => (
                  <React.Fragment key={inv._id}>
                    <TableRow className="bg-amber-50/30 hover:bg-amber-50">
                      <TableCell>
                        <button className="font-bold text-sm text-blue-700 hover:underline flex items-center gap-1" onClick={() => toggleInvoice(inv._id)}>
                          {expandedInvoice === inv._id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          {inv.invoice_number}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs">{inv.date?.slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">{inv.lines?.length || 0}</TableCell>
                      <TableCell className="text-xs">{inv.total_weight_kg?.toFixed(1)} kg</TableCell>
                      <TableCell className="text-xs font-semibold">{inv.total_vnd?.toLocaleString()}đ</TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={() => onDownloadPdf?.(inv._id, inv.invoice_number)}>
                          <Download size={12} /> PDF
                        </Button>
                      </TableCell>
                      {canPay && (
                        <TableCell>
                          <Checkbox checked={false} onCheckedChange={() => onMarkPaid?.(inv._id)} />
                        </TableCell>
                      )}
                      {canDelete && (
                        <TableCell>
                          <Button data-testid={`delete-invoice-${inv.invoice_number}`} size="sm" variant="ghost" className="h-6 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                            onClick={() => onDeleteInvoice?.(inv._id, inv.invoice_number)}>
                            <Trash2 size={12} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                    {expandedInvoice === inv._id && (
                      <TableRow>
                        <TableCell colSpan={canDelete ? 8 : canPay ? 7 : 6} className="bg-slate-50 p-0">
                          <div className="px-6 py-3">
                            <p className="text-xs font-semibold text-slate-500 mb-2">{t('package')} :</p>
                            {(inv.lines || []).map((line, i) => (
                              <div key={i} className="flex items-center gap-4 text-xs py-1 border-b border-slate-100 last:border-0">
                                {line.item_type === 'package' && <Package size={12} className="text-blue-500" />}
                                <span className="font-medium text-slate-700">{line.order_numbers?.join(', ')}</span>
                                <span className="text-slate-500">{line.weight_kg} kg</span>
                                <span className="font-semibold">{line.total_vnd?.toLocaleString()}đ</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ===== 3. ARCHIVES FACTURES PAYÉES ===== */}
      <Card className="border-slate-200 shadow-sm" data-testid="paid-invoices-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-emerald-600" />
            <CardTitle className="text-base font-medium">{t('invoice_paid')}</CardTitle>
            {paidInvoices.length > 0 && (
              <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs">{paidInvoices.length}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {paidInvoices.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-3">{t('no_invoices')}</p>
          ) : (
            <div className="space-y-2">
              {paidInvoices.map(inv => (
                <div key={inv._id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div
                    role="button"
                    tabIndex={0}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left cursor-pointer"
                    onClick={() => toggleInvoice(inv._id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') toggleInvoice(inv._id); }}
                  >
                    <div className="flex items-center gap-3">
                      {expandedInvoice === inv._id ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />}
                      <span className="font-bold text-slate-900">N°{inv.invoice_number}</span>
                      <span className="text-xs text-slate-500">{inv.date?.slice(0, 10)}</span>
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">{t('invoice_paid')}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-slate-600">{inv.lines?.length || 0} {t('package').toLowerCase()}</span>
                      <span className="text-slate-600">{inv.total_weight_kg?.toFixed(1)} kg</span>
                      <span className="font-bold text-slate-900">{inv.total_vnd?.toLocaleString()}đ</span>
                      <Button size="sm" variant="outline" className="h-6 text-xs gap-1 ml-2" onClick={(e) => { e.stopPropagation(); onDownloadPdf?.(inv._id, inv.invoice_number); }}>
                        <Download size={12} /> PDF
                      </Button>
                      {canPay && (
                        <Checkbox checked={true} onCheckedChange={() => onMarkUnpaid?.(inv._id)} className="ml-1" />
                      )}
                    </div>
                  </div>
                  {expandedInvoice === inv._id && (
                    <div className="px-6 py-3 bg-white border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 mb-2">{t('package')} :</p>
                      {(inv.lines || []).map((line, i) => (
                        <div key={i} className="flex items-center gap-4 text-xs py-1.5 border-b border-slate-100 last:border-0">
                          <Package size={12} className="text-slate-400" />
                          <span className="font-medium text-slate-700">{line.order_numbers?.join(', ')}</span>
                          <span className="text-slate-500">{line.weight_kg} kg</span>
                          <span className="font-semibold">{line.total_vnd?.toLocaleString()}đ</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
